import { Skel } from '@/ui';
import { useState } from 'react';
import './Organizacion.css';

// Demo constants removed — the route passes real org data as props.
// Empty fallbacks are used below so components render an empty state rather
// than fabricated names (mirrors the earlier MOVIMIENTOS_DEFAULT cleanup).

function RoleBadge({ rol, variant }) {
  return (
    <span
      className={['org-role-badge', variant === 'owner' ? 'owner' : '']
        .filter(Boolean)
        .join(' ')}
    >
      {rol}
    </span>
  );
}

function StatusDotPill({ estado, label }) {
  return (
    <span className={['org-status-dot-pill', estado].filter(Boolean).join(' ')}>
      <span className="org-status-dot-pill__d" />
      {label}
    </span>
  );
}

// Per-row hierarchy: nobody acts on their own row or on the owner's, and only
// the owner acts on another admin. The API enforces the same rules with 403
// OwnerOnlyAction, CannotModifySelf and CannotModifyOwner. This check keeps the
// button from sending a request the API would refuse.
function rowBlockedReason(m, { actingIsOwner, currentUserId }) {
  if (m.userId != null && m.userId === currentUserId) {
    return 'No puedes actuar sobre tu propia cuenta.';
  }
  if (m.rolVariant === 'owner') {
    return 'El propietario no se puede modificar.';
  }
  if (m.esAdmin && !actingIsOwner) {
    return 'Solo el propietario actúa sobre otro administrador.';
  }
  return null;
}

export function MiembrosTab({
  miembros,
  onVerMiembro,
  onInvitar,
  loading,
  // Role gate: only Administrador users may invite, change role, or remove
  // members. Defaults to true so Storybook / tests that omit it keep actions
  // enabled. Pass false for non-admin roles to hide the mutating controls.
  canManageTeam = true,
  // Whether the acting user is the owner, and their user id, both read by
  // rowBlockedReason.
  actingIsOwner = false,
  currentUserId,
  // Called with the self row when a non-owner member leaves the organization.
  // Only the self row offers it, and never to the owner.
  onLeave,
  // Replaces onVerMiembro on an INVITED row, since a member who has not
  // accepted has nothing to show under "Ver".
  onReenviar,
  // Optional: total active-member count for the panel-head badge. When the
  // caller paginates `miembros` (passing only the current page), it should also
  // pass the full active count here so the badge reflects the whole team, not
  // just the visible page. Falls back to the active count of `miembros`.
  totalActivos,
}) {
  // Every resend rotates the invitation token, so a second click while the
  // first is in flight would invalidate the email just sent. One resend runs
  // at a time: every pending row waits, or a click on another row would clear
  // the guard of the first while its request is still out.
  const [resendingId, setResendingId] = useState(null);
  async function handleResend(m) {
    setResendingId(m.id);
    try {
      await onReenviar?.(m);
    } finally {
      setResendingId(null);
    }
  }
  const activos =
    totalActivos ?? miembros.filter((m) => m.estado === 'active').length;

  return (
    <section className="org-settings-section">
      {/* Single "Equipo" header lives in the panel head below (count + Invitar)
          — no separate outer "Miembros" title. */}
      <div className="org-panel-with-head">
        <div className="org-panel-head">
          <h3>Equipo</h3>
          <span className="org-panel-count">{loading ? '—' : activos}</span>
          {canManageTeam ? (
            <button className="org-cta" onClick={onInvitar} type="button">
              + Invitar miembro
            </button>
          ) : (
            <button
              className="org-cta org-cta--disabled"
              type="button"
              disabled
              title="Solo administradores"
            >
              + Invitar miembro
            </button>
          )}
        </div>
        <div className="org-panel-scroll">
          <table className="org-simple-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Ingreso</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(4)].map((_, i) => (
                    <tr key={`skel-${i}`}>
                      <td>
                        <Skel w={70} />
                      </td>
                      <td>
                        <Skel w={90} />
                      </td>
                      <td>
                        <Skel w={50} />
                      </td>
                      <td>
                        <Skel h={22} w={50} />
                      </td>
                      <td>
                        <Skel h={22} w={50} />
                      </td>
                      <td>
                        <Skel h={22} w={30} />
                      </td>
                    </tr>
                  ))
                : miembros.map((m) => {
                    const blockedReason = canManageTeam
                      ? rowBlockedReason(m, {
                          actingIsOwner,
                          currentUserId,
                        })
                      : 'Solo administradores';
                    const isSelf =
                      m.userId != null && m.userId === currentUserId;
                    return (
                      <tr key={m.id}>
                        <td>{m.nombre}</td>
                        <td className="org-cell-mono">{m.correo}</td>
                        <td className="org-cell-mono org-cell-muted">
                          {m.ingreso}
                        </td>
                        <td>
                          <RoleBadge rol={m.rol} variant={m.rolVariant} />
                        </td>
                        <td>
                          <StatusDotPill
                            estado={m.estado}
                            label={m.estadoLabel}
                          />
                        </td>
                        <td>
                          <span className="org-row-actions">
                            {blockedReason ? (
                              <button
                                className="row-action row-action--disabled"
                                type="button"
                                disabled
                                title={blockedReason}
                              >
                                {m.accion}
                              </button>
                            ) : (
                              <button
                                className="row-action"
                                type="button"
                                disabled={
                                  m.estado === 'pending' && resendingId != null
                                }
                                onClick={() =>
                                  m.estado === 'pending'
                                    ? handleResend(m)
                                    : onVerMiembro && onVerMiembro(m)
                                }
                              >
                                {m.accion}
                              </button>
                            )}
                            {isSelf && !actingIsOwner && (
                              <button
                                className="row-action danger"
                                type="button"
                                onClick={() => onLeave && onLeave(m)}
                              >
                                Salir
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
